/**
 * Cliente del Web Worker con una interfaz mínima (WorkerLike) inyectable, para poder
 * probar el protocolo de mensajes (progreso, resultado, cancelación) sin un hilo real.
 */
import type { EngineInput, EngineResult } from "../domain/types";
import type { WorkerInboundMessage, WorkerOutboundMessage } from "./protocol";

export interface WorkerLike {
  postMessage(message: WorkerInboundMessage): void;
  addEventListener(
    type: "message",
    listener: (event: MessageEvent<WorkerOutboundMessage>) => void,
  ): void;
  removeEventListener(
    type: "message",
    listener: (event: MessageEvent<WorkerOutboundMessage>) => void,
  ): void;
  terminate(): void;
}

export type WorkerFactory = () => WorkerLike;

export function createDefaultWorker(): WorkerLike {
  return new Worker(new URL("./engine.worker.ts", import.meta.url), {
    type: "module",
  }) as unknown as WorkerLike;
}

export interface RunHandle {
  promise: Promise<EngineResult>;
  cancel: () => void;
}

export class ToolCallWorkerClient {
  private readonly worker: WorkerLike;

  constructor(factory: WorkerFactory = createDefaultWorker) {
    this.worker = factory();
  }

  run(runId: string, input: EngineInput, onProgress: (value: number) => void): RunHandle {
    let settled = false;

    const promise = new Promise<EngineResult>((resolve) => {
      const listener = (event: MessageEvent<WorkerOutboundMessage>): void => {
        const message = event.data;
        if (message.runId !== runId) return;

        if (message.type === "PROGRESS") {
          onProgress(message.value);
          return;
        }

        if (message.type === "RESULT") {
          settled = true;
          this.worker.removeEventListener("message", listener);
          resolve(message.result);
          return;
        }

        if (message.type === "CANCELLED") {
          settled = true;
          this.worker.removeEventListener("message", listener);
          resolve({
            ok: false,
            error: {
              code: "RUN_CANCELLED",
              message: "La ejecución fue cancelada por el visitante.",
              details: [],
              correlationId: runId,
              recoverable: true,
            },
          });
        }
      };

      this.worker.addEventListener("message", listener);
      this.worker.postMessage({ type: "RUN", runId, input });
    });

    const cancel = (): void => {
      if (settled) return;
      this.worker.postMessage({ type: "CANCEL", runId });
    };

    return { promise, cancel };
  }

  dispose(): void {
    this.worker.terminate();
  }
}
